// AppLogin.js
import React, { useState } from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { useMutation} from '@apollo/client';
import HomeplusBigLogo from '../../asset/image/homeplus.svg';
import VzmTextField from '../../common/VzmTextField';
import { VzmInputBlock, NonCapButton } from '../../common/VzmStyled';
import { AppLoginDict } from './AppLoginDict';
import { ApiMsg, AppObject, AppStyle, AppWord } from '../../value/AppConst';
import InputUtil from '../../value/InputUtil';
import { useAlertSetter } from '../../api/app-hook';
import { LOGIN_NEW_TOP } from './AppLoginGql';
import UserSessionInfo from '../../value/UserSessionInfo';
import { useSnackbar } from 'notistack';
import UserAllocControl from '../user_alloc/UserAllocControl';
import UserAllocCheck from '../user_alloc/UserAllocCheck';
import UserInfoPasswordOffline from '../user_info/UserInfoPasswordOffline';
import ForgotPassword from './ForgotPassword';

const LogoWidth = 360;
const LogoRightMargin = 40;
const LoginBoxWidth = 520;

const OutterBox = styled(Box)({
    display: 'flex', flexDirection:'column', justifyContent: 'center', alignItems: 'center', height: '100vh',
});

const InnerBox = styled(Box)({
    display: 'inline-flex', width: (LoginBoxWidth + LogoWidth + LogoRightMargin), marginBottom: 100,
});

const LogoBox = styled(Box)({flexGrow: 1,});

const LoginBox = styled(Box)({
    width: LoginBoxWidth, display: 'flex', flexDirection: 'column',
});

const TopText = styled(Box)({
    fontSize: 28, fontWeight: 'bold', fontFamily: 'Times New Roman', fontStyle: 'italic',
});

const ButtonBlock = styled(Box)({
    display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 20,
});

const SubButton = styled(Button)({
    margin: '0px 5px 0px 5px', flexGrow: 1,
    '&:first-of-type': {marginLeft: 0},
    '&:last-of-type': {marginRight: 0},
});

const DevBox = styled(Box)({
    position: 'fixed', bottom: 50, left: 20, right: 20, padding: 5,
    borderRadius: 5, border: '1px solid #f00', textAlign: 'center', 
});

const inputMap = AppLoginDict.inputMap;

/**
 * 사용자 로그인 화면.
 * 로그인 여부의 확인은 상위 컴포넌트에서 처리한다.
 * 로그인 성공하면 상위 컴포넌트로 확인 메시지 전달한다.
 * @returns 
 */
export default function AppLogin({
    onLoginSuccess
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [openUserAlloc, setOpenUserAlloc] = useState(false);
    const [openUserAllocCheck, setOpenUserAllocCheck] = useState(false);
    const [openChangePasswordOffline, setOpenChangePasswordOffline] = useState(false);
    const [openForgotPassword, setOpenForgotPassword] = useState(false);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [loginNewTop, responseLogin] = useMutation(LOGIN_NEW_TOP, {
        onCompleted: (data) => {onCompleteLogin(data)},
        onError: (error) => {addAlert({title:"로그인 중 오류", message:[error.message]})},
    });

    const isLocal = window.location.hostname === 'localhost';

    const onCompleteLogin = (data) => {
        if(data.loginNewTop) {
            if(data.loginNewTop.sessionNote === ApiMsg.OK) {
                const sessionInfo = UserSessionInfo.initFromUserObject(data.loginNewTop);
                // userInfoRepo(sessionInfo);
                enqueueSnackbar(sessionInfo.user.userName + '님 환영합니다.', {variant: 'success'});
                onLoginSuccess(sessionInfo);
            }
            else {
                addAlert({
                    title: '로그인 실패',
                    message: [
                        '로그인에 실패했습니다.',
                        data.loginNewTop.sessionNote
                    ]
                })
            }
        }
    };

    const onChangeValue = (id, value, error) => {
        setInput(InputUtil.reflectData(input, id, value, error, inputMap, false));
    };

    const onClickLogin = () => {
        const param = {variables: {userId: input.data.userId, password: input.data.password}};
        const newInput = {...input};
        newInput.data.password = '';
        setInput(newInput);
        loginNewTop(param);
    };

    const onClickDevSetUser = (id, pswd) => {
        const newInput = {...input};
        newInput.data.userId = id;
        newInput.data.password = pswd;
        newInput.changed = true;
        setInput(newInput);
    };

    const renderTextTOP = () => {
        return(
            <TopText>
                <span style={{color:"red"}}>T</span>ransport &nbsp;
                <span style={{color:"orange"}}>O</span>peration &nbsp;
                <span style={{color:"green"}}>P</span>rogram
            </TopText>
        );
    };

    if(openForgotPassword) {
        return(
            <ForgotPassword onClose={()=>setOpenForgotPassword(false)} />   
        )
    }
    return(
        <OutterBox>
            <InnerBox>
                <LogoBox>
                    <img src={HomeplusBigLogo} alt="Homeplus" width={LogoWidth}
                        style={{marginRight: LogoRightMargin}}
                    />
                    {renderTextTOP()}
                </LogoBox>
                <LoginBox>
                    <Box style={{flexGrow:1}}>
                        &nbsp;
                    </Box>
                    <form>
                    <VzmInputBlock>
                        <VzmTextField dict={inputMap.userId}
                            value={input.data.userId}
                            onChange={onChangeValue}
                            fullWidth={true}
                            inputProps={{autoComplete:"userId"}}
                        />
                    </VzmInputBlock>
                    <VzmInputBlock>
                        <VzmTextField dict={inputMap.password}
                            value={input.data.password}
                            onChange={onChangeValue}
                            onEnterKey={()=>{if(input.data.password) onClickLogin()}}
                            fullWidth={true}
                            inputProps={{autoComplete: 'current-password'}}
                        />
                    </VzmInputBlock>
                    <VzmInputBlock>
                        <Button variant={AppStyle.mui.contained} color={AppStyle.mui.primary}
                            size={AppWord.LARGE}
                            onClick={onClickLogin}
                            startIcon={<LoginIcon/>}
                            fullWidth
                            disabled={
                                input.hasError || !input.data.userId || !input.data.password
                                || responseLogin.loading
                            }
                        >
                            로그인
                        </Button>
                    </VzmInputBlock>
                    </form>
                    <ButtonBlock>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.secondary}
                            onClick={()=>setOpenUserAlloc(true)}
                        >
                            승인요청
                        </SubButton>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.success}
                            onClick={()=>setOpenUserAllocCheck(true)}
                        >
                            승인요청 확인
                        </SubButton>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.warning}
                            onClick={()=>setOpenChangePasswordOffline(true)}
                        >
                            사용자 암호 변경
                        </SubButton>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.info}
                            onClick={()=>setOpenForgotPassword(true)}
                        >
                            메일로 암호 재설정
                        </SubButton>
                    </ButtonBlock>
                </LoginBox>
            </InnerBox>
            {
                isLocal &&
                <DevBox>
                    <NonCapButton
                        onClick={()=>onClickDevSetUser('home1', '4321')}
                        variant='contained' color={AppStyle.mui.warning}
                    >
                        home1
                    </NonCapButton>
                    <NonCapButton
                        onClick={()=>onClickDevSetUser('hmn00', 'asdf123')}
                        variant='contained' color={AppStyle.mui.warning}
                        style={{marginLeft: 5}}
                    >
                        hmn00(점포)
                    </NonCapButton>
                    {
                        Array.from({length: 10}).map((_, idx) => {
                            return(
                                <NonCapButton
                                    key={idx}
                                    onClick={()=>onClickDevSetUser('etrace'+(idx), 'home+400')}
                                    variant='contained'
                                    color={
                                        idx >= 6 && idx <= 8 ?
                                        AppStyle.mui.inherit :
                                        AppStyle.mui.warning
                                    }
                                    style={{marginLeft: 5}}
                                    title={'etrace'+(idx)}
                                >
                                    e.{idx} {idx===6 ? 'st' : idx===7 ? 'tr' : idx===8 ? 'dr' : null}
                                </NonCapButton>
                            )
                        })
                    }
                    <NonCapButton
                        onClick={()=>onClickDevSetUser('hmnklp1', '111111')}
                        variant='contained' color={AppStyle.mui.warning}
                        style={{marginLeft: 5}}
                    >
                        hmnklp1(운송사F)
                    </NonCapButton>
                    <NonCapButton
                        onClick={()=>onClickDevSetUser('yonghyunkwon', 'rnjs1516')}
                        variant='contained' color={AppStyle.mui.warning}
                        style={{marginLeft: 5}}
                    >
                        yonghyunkwon(운송사E)
                    </NonCapButton>
                    <NonCapButton
                        onClick={()=>onClickDevSetUser('daehan2', '222222+')}
                        variant='contained' color={AppStyle.mui.warning}
                        style={{marginLeft: 5}}
                    >
                        daehan2(운송사A)
                    </NonCapButton>
                </DevBox>
            }
            <UserAllocControl
                open={openUserAlloc}
                onClose={()=>setOpenUserAlloc(false)}
            />
            <UserAllocCheck
                open={openUserAllocCheck}
                onClose={()=>setOpenUserAllocCheck(false)}
            />
            <UserInfoPasswordOffline
                open={openChangePasswordOffline}
                onClose={()=>setOpenChangePasswordOffline(false)}
            />
        </OutterBox>
    );
}

// hmn00	6	916	함안ADC	asdf123